<template>
  <a
    :href="block.item.url"
    rel="nofollow noindex"
    target="_blank"
    @click.prevent="onClick(block, Modal)"
  >
    <Image
      v-if="block.item.featured_image"
      :media="block.item.featured_image"
      class="ag-block__featured-image"
      conversion="cover"
      lazy
      responsive
    />
    <div class="ag-block__body">
      <div class="ag-block__header">
        <Icon
          :icon="block.item.type.icon"
          class="ag-block__type-icon"
        />
        <span class="ag-block__type-label">{{ block.item.type.name }}</span>
      </div>
      <div class="ag-block__content">
        <div class="ag-block__content-inner">
          <Markdown
            v-show="showTitle"
            as="h3"
            class="ag-block__title ag-content__title"
            :text="block.item.title"
          />
          <p
            v-if="showDescription"
            class="ag-block__description"
          >
            <strong
              v-if="showLeadIn"
              class="ag-block__lead-in"
            >{{ block.item.lead_in }}</strong>
            <Markdown
              v-if="showSynopsis"
              as="span"
              class="ag-block__synopsis"
              :text="' ' + block.item.synopsis"
            />
            <!-- <em v-if="showAuthor" class="ag-block__author"> By {{ item.author }}.</em> -->
          </p>
          <div class="ag-block__footer">
            <div class="ag-block-more">
              <span class="ag-block-more__label">{{ block.item.call_to_action ?? "Read More" }}</span>
              <Icon
                icon="long-arrow-right"
                class="ag-block-more__icon"
              />
            </div>
            <div
              v-if="showRuntime"
              class="ag-block__runtime"
            >{{ block.item.runtime }}m</div>
          </div>
        </div>
      </div>
    </div>
  </a>
</template>

<script setup lang="ts">
import type { ContentBlock } from '~/types/blocks'
import type { Collection } from '~/types/collection'
import type { Customisations } from '~/types/customisations'

const props = defineProps<{
  block: ContentBlock
  collection: Collection
  customisations: Customisations
}>()

const { content_author_enabled, content_lead_in_enabled, content_runtime_enabled, content_synopsis_enabled, content_title_enabled } = toRefs(
  props.customisations
)

const showTitle = computed(() => content_title_enabled.value && props.block.item.title && props.block.item.url)
const showDescription = computed(() => showLeadIn.value || showSynopsis.value || showAuthor.value || showRuntime.value)
const showLeadIn = computed(() => content_lead_in_enabled.value && props.block.item.lead_in)
const showRuntime = computed(() => content_runtime_enabled.value && props.block.item.runtime)
const showAuthor = computed(() => content_author_enabled.value && props.block.item.author)
const showSynopsis = computed(() => content_synopsis_enabled.value && props.block.item.synopsis)

// Modal
const Modal = resolveComponent('LazyModernelloModal')
const { onClick } = useBlock()
</script>

<style>
.ag-theme--modernello .ag-block--content {
  @apply block cursor-pointer bg-gray-900 transition;

  .ag-block__featured-image {
    @apply absolute inset-0 h-full w-full object-cover opacity-90 transition;
  }

  .ag-block__body {
    @apply relative flex h-full flex-col justify-between transition;
  }

  .ag-block__header {
    @apply flex items-center space-x-3 p-8;
  }

  .ag-block__type-icon,
  .ag-block__type-label {
    @apply text-white/60 transition-colors;
  }

  .ag-block__type-icon {
    @apply text-2xl;
  }

  .ag-block__type-label {
    @apply text-xs font-bold uppercase;
  }

  .ag-block__content {
    @apply mt-12 bg-gradient-to-t from-gray-900 to-transparent p-8;
  }

  .ag-block__title {
    @apply text-white;
  }

  .ag-block__description,
  .ag-block__runtime {
    @apply text-white/80;
  }

  .ag-block__description {
    @apply mb-0 mt-2 line-clamp-3 p-0;
  }

  .ag-block__runtime {
    @apply font-medium;
  }

  .ag-block__footer {
    @apply mt-6 flex items-center space-x-4 text-xs text-white;
  }

  .ag-block-more {
    @apply flex items-center space-x-3 font-bold;

    &__label {
      @apply uppercase text-white;
    }
  }

  &:hover {
    .ag-block__featured-image {
      @apply scale-110;
    }

    .ag-block__type-icon,
    .ag-block__type-label {
      @apply text-white;
    }

    .ag-block__body {
      @apply ring-4 ring-inset ring-white/25;
    }

    .ag-block-more__icon {
      @apply animate-bounce-x;
    }
  }
}
</style>
